import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { Category } from '@omniafishing/core';
import classNames from 'classnames';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import CategoryProductsGridContainer from '../components/category_products_grid/category_products_grid_container';
import { ContentWrapper } from '../components/content_wrapper/content_wrapper';
import { OmniaBreadcrumb } from '../components/omnia_breadcrumb/omnia_breadcrumb';
import { ScrollToTop } from '../components/scroll_to_top/scroll_to_top';
import { SEO } from '../components/seo/seo';
import { RoutePaths } from '../routes';
import base from '../styles/base.less';
import styles from './category_page.less';

interface CategoryPageProps {
  category: Category;
  query: string;
}

const CategoryPage = (props: CategoryPageProps) => {
  const { category, query } = props;
  const [showMore, setShowMore] = useState(false);

  const {
    banner_image,
    description,
    display_name,
    meta_description,
    meta_title,
    page_heading,
    page_subheading,
    url_path,
    subcategories,
  } = category;

  const toggleShowMore = () => {
    setShowMore(!showMore);
  };

  const descriptionSplit = description ? description.split(' ') : [''];
  const descriptionIsLong = descriptionSplit.length > 100;

  let descriptionHtml = description;
  if (!showMore && descriptionIsLong) {
    descriptionHtml = descriptionSplit.slice(0, 100).join(' ') + '...';
  }

  const breadcrumbs = (
    <OmniaBreadcrumb
      items={[
        {
          url: `${RoutePaths.SHOP}`,
          name: 'Products',
        },
        {
          name: display_name,
          url: null,
        },
      ]}
    />
  );

  return (
    <ScrollToTop>
      <section className={styles.CategoryPage}>
        <SEO titleTemplate="%s" title={meta_title || display_name} description={meta_description}>
          {banner_image && (
            <link rel="preload" as="image" href={banner_image} {...{ fetchpriority: 'high' }} />
          )}
        </SEO>

        {banner_image ? (
          <header style={{ backgroundImage: `url(${banner_image})` }} className={styles.header}>
            <ContentWrapper>
              <h1 className={styles.headerHeading}>{page_heading || display_name}</h1>
              <ul className={styles.subcategories}>
                {subcategories
                  .filter((s) => !s.nav_hidden)
                  .map((subcategory) => (
                    <li key={subcategory.name} className={styles.subcategory}>
                      <Link to={`${RoutePaths.CATEGORIES}/${url_path}/${subcategory.url_path}`}>
                        <div className={styles.subcategoryImg}>
                          <img
                            src={subcategory.image}
                            alt={subcategory.img_alt || subcategory.display_name}
                          />
                        </div>
                        <span className={styles.subcategoryName}>{subcategory.display_name}</span>
                      </Link>
                    </li>
                  ))}
              </ul>
              <div className={styles.headerBreadcrumbs}>{breadcrumbs}</div>
            </ContentWrapper>
          </header>
        ) : (
          <ContentWrapper>
            <div className={styles.breadcrumbs}>{breadcrumbs}</div>
          </ContentWrapper>
        )}
        <section>
          <ContentWrapper>
            <h1 className={base.h1}>{page_heading || display_name}</h1>
            {page_subheading && <h2 className={base.h2}>{page_subheading}</h2>}
            <div>
              <div
                className={styles.description}
                dangerouslySetInnerHTML={{ __html: descriptionHtml }}
              />
              {descriptionIsLong && (
                <span className={classNames(base.link, styles.moreLink)} onClick={toggleShowMore}>
                  {showMore ? (
                    <>
                      <UpOutlined />
                      Show Less
                    </>
                  ) : (
                    <>
                      <DownOutlined />
                      Show More
                    </>
                  )}
                </span>
              )}
            </div>
          </ContentWrapper>
        </section>

        <CategoryProductsGridContainer category={category} query={query} key={url_path} />
      </section>
    </ScrollToTop>
  );
};

export default CategoryPage;
